import type {CSSProperties} from 'react';
import type {CropFrame} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import {CropHandle} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import {CustomControlCursor} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';

export interface CropHandlesOverlayProps {
  pageHashedId: string;
}

/** The box's inline style, plus the custom property its SCSS reads to pull corner brackets past the outline. */
export interface CropBoxStyle extends CSSProperties {
  '--crop-outline-width': string;
}

export interface CropHandleSpec {
  handle: CropHandle;
  /** Position along the frame's own axes, as a fraction of its width and height. */
  fractionX: number;
  fractionY: number;
  cursor: CustomControlCursor;
  isCorner: boolean;
}

/**
 * The four corners, then the four edge midpoints. Edge handles are hidden while a ratio is locked -
 * an edge drag has only one axis to offer, so it cannot hold a ratio without fighting the user.
 */
/** A handle spec's fraction along an axis when the handle sits mid-edge and moves neither end of that axis. */
export const CROP_HANDLE_MIDDLE_FRACTION = 0.5;

export const CROP_HANDLE_SPECS: CropHandleSpec[] = [
  {handle: CropHandle.TOP_LEFT, fractionX: 0, fractionY: 0, cursor: CustomControlCursor.CURSOR_NWSE_RESIZE, isCorner: true},
  {handle: CropHandle.TOP_RIGHT, fractionX: 1, fractionY: 0, cursor: CustomControlCursor.CURSOR_NESW_RESIZE, isCorner: true},
  {handle: CropHandle.BOTTOM_LEFT, fractionX: 0, fractionY: 1, cursor: CustomControlCursor.CURSOR_NESW_RESIZE, isCorner: true},
  {handle: CropHandle.BOTTOM_RIGHT, fractionX: 1, fractionY: 1, cursor: CustomControlCursor.CURSOR_NWSE_RESIZE, isCorner: true},
  {handle: CropHandle.MIDDLE_LEFT, fractionX: 0, fractionY: CROP_HANDLE_MIDDLE_FRACTION, cursor: CustomControlCursor.CURSOR_EW_RESIZE, isCorner: false},
  {handle: CropHandle.MIDDLE_RIGHT, fractionX: 1, fractionY: CROP_HANDLE_MIDDLE_FRACTION, cursor: CustomControlCursor.CURSOR_EW_RESIZE, isCorner: false},
  {handle: CropHandle.MIDDLE_TOP, fractionX: CROP_HANDLE_MIDDLE_FRACTION, fractionY: 0, cursor: CustomControlCursor.CURSOR_NS_RESIZE, isCorner: false},
  {handle: CropHandle.MIDDLE_BOTTOM, fractionX: CROP_HANDLE_MIDDLE_FRACTION, fractionY: 1, cursor: CustomControlCursor.CURSOR_NS_RESIZE, isCorner: false},
];

export enum CropDragKind {
  /** Resizing the crop window by one of its eight handles. */
  WINDOW = 'window',
  /** Moving the crop window by a press that met a handle's finger padding deep inside the window rather than its mark. */
  MOVE = 'move',
  /** Scaling the full image by one of its four corner handles. */
  IMAGE = 'image',
  /** Turning the picture behind the fixed window by its rotation handle. */
  ROTATE = 'rotate',
}

export interface CropHandleDragTarget {
  kind: CropDragKind.WINDOW | CropDragKind.IMAGE;
  spec: CropHandleSpec;
}

/** A rotation drag remembers where it started, so the picture turns by exactly the pointer's travel around the window centre. */
export interface CropRotationDragTarget {
  kind: CropDragKind.ROTATE;
  startPointerAngle: number;
  startRotation: number;
}

export interface CropWindowMoveDragTarget {
  kind: CropDragKind.MOVE;
  startPointerScene: ScenePoint;
  startWindowCentreScene: ScenePoint;
}

export type CropDragTarget = CropHandleDragTarget | CropRotationDragTarget | CropWindowMoveDragTarget;

/**
 * A press on a window handle this close, in screen pixels, to the edge or corner the handle moves is a
 * resize; deeper inside the window it moves the window instead. A finger-sized handle reaches well into
 * a small window, so the reach also shrinks with the window.
 */
export const CROP_HANDLE_GRAB_REACH_PX = 28;
export const CROP_HANDLE_GRAB_REACH_WINDOW_FRACTION = 1 / 3;

export const CROP_ROTATION_CONTROL_ID = 'crop-rotation-control';
/** The picture's rotation snaps to these steps when the drag comes within the tolerance, as the item rotation handle does. */
export const CROP_ROTATION_SNAP_STEP_DEGREES = 45;
export const CROP_ROTATION_SNAP_TOLERANCE_DEGREES = 2;

/** A point in screen pixels, relative to the controls layer. */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** A point in the canvas' scene units, the space the crop frames are published in. */
export interface ScenePoint {
  x: number;
  y: number;
}

/** A press that landed on a crop window handle, judged against the reach of the edges that handle moves. */
export interface CropHandlePress {
  frame: CropFrame;
  spec: CropHandleSpec;
  scenePoint: ScenePoint;
  /** Scene units; see `getHandleGrabReach`. */
  reach: number;
}

/**
 * How close, in screen pixels, a crop-window corner may come to the image's same corner before the
 * image handle there is hidden: the two would otherwise stack and fight for the pointer.
 */
export const CROP_IMAGE_HANDLE_HIDE_DISTANCE_PX = 24;

export const CROP_IMAGE_CORNER_CONTROL_ID_PREFIX = 'crop-image-corner-';
/** The image's scale handles: a little larger than an item's 14px square, with every edge rounded, per the design. */
export const CROP_IMAGE_CORNER_SIDE = 18;
export const CROP_IMAGE_CORNER_RADIUS = '6px';
